import {
  deriveIntroMadeProjectFollowUpDate,
  deriveWarmIntroFollowUpLane,
  deriveWorkflowFlags,
} from "../../../shared/lib/ev-workflow-policy.ts";
import type {
  EvIntroContactUpdateInput,
  EvWorkflowPatch,
  EvWorkstationProject,
} from "../types/index.ts";

function normalizeNullableString(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const output: string[] = [];
  for (const value of values) {
    const normalized = value?.replace(/\s+/g, " ").trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    output.push(normalized);
  }
  return output;
}

function formatWarmLeadContact(item: EvWorkstationProject["warmLead"]["matches"][number]): string {
  return `${item.contact.name}${item.contact.role ? ` (${item.contact.role})` : ""} @ ${item.fundName}`;
}

function hasWarmLeadConversationHistory(workflow: EvWorkstationProject["workflow"]): boolean {
  return Boolean(
    normalizeNullableString(workflow.groupChatUrl) ||
      normalizeNullableString(workflow.transcriptUrl) ||
      normalizeNullableString(workflow.callSummary) ||
      workflow.lastContactAt
  );
}

function hasWarmLeadOperatorNotes(workflow: EvWorkstationProject["workflow"]): boolean {
  return Boolean(normalizeNullableString(workflow.internalNotes));
}

function hasWarmLeadScheduledFollowUp(workflow: EvWorkstationProject["workflow"]): boolean {
  return Boolean(
    normalizeNullableString(workflow.nextAction) ||
      workflow.projectFollowUpDate ||
      workflow.warmIntroFollowUpDate ||
      workflow.followUpDate ||
      workflow.nextTouchpointAt ||
      workflow.nextTouchpointCalendarEventId ||
      workflow.nextTouchpointType !== "unclear" ||
      workflow.nextTouchpointChannel !== "unclear"
  );
}

function hasIntroTrackingActivity(introContacts: EvWorkstationProject["introContacts"]): boolean {
  return introContacts.some(
    (contact) =>
      contact.status !== "not_asked" || Boolean(normalizeNullableString(contact.notes ?? null))
  );
}

function summarizeIntroTracking(
  introContacts: EvWorkstationProject["introContacts"]
): string | null {
  const trackedContacts = introContacts.filter(
    (contact) =>
      contact.status !== "not_asked" || Boolean(normalizeNullableString(contact.notes ?? null))
  );
  if (trackedContacts.length === 0) return null;

  const preview = trackedContacts.slice(0, 2).map((contact) => {
    const label = contact.status.replaceAll("_", " ");
    return `${contact.contact.name} ${label}`;
  });

  return `Intro tracking: ${preview.join("; ")}${trackedContacts.length > preview.length ? ` (+${trackedContacts.length - preview.length} more)` : ""}.`;
}

function getWarmLeadOutreachGaps(
  workflow: EvWorkstationProject["workflow"],
  introContacts: EvWorkstationProject["introContacts"]
): string[] {
  return uniqueStrings([
    !workflow.lastContactAt ? "No last contact is recorded." : null,
    !hasWarmLeadConversationHistory(workflow) ? "No conversation history is attached." : null,
    !hasWarmLeadOperatorNotes(workflow) ? "No operator notes are recorded." : null,
    !hasWarmLeadScheduledFollowUp(workflow) && !hasIntroTrackingActivity(introContacts)
      ? "No follow-up or next action is scheduled."
      : null,
  ]);
}

function isUntouchedWarmLead(
  workflow: EvWorkstationProject["workflow"],
  introContacts: EvWorkstationProject["introContacts"]
): boolean {
  return (
    workflow.stage === "lead" &&
    !hasWarmLeadConversationHistory(workflow) &&
    !hasWarmLeadOperatorNotes(workflow) &&
    !hasWarmLeadScheduledFollowUp(workflow) &&
    !hasIntroTrackingActivity(introContacts)
  );
}

function buildWarmLeadSummary(
  item: EvWorkstationProject,
  workflow: EvWorkstationProject["workflow"],
  introContacts: EvWorkstationProject["introContacts"]
): string | null {
  if (item.warmLead.matches.length === 0 && item.warmLead.matchedFundNames.length === 0) {
    return null;
  }

  const contactPreview = uniqueStrings(
    item.warmLead.matches.slice(0, 4).map(formatWarmLeadContact)
  );
  const introPath =
    contactPreview.length > 0
      ? `Warm lead via ${contactPreview.join("; ")}.`
      : `Warm lead via matching funds ${item.warmLead.matchedFundNames.join(", ")}.`;

  const introTrackingSummary = summarizeIntroTracking(introContacts);

  if (isUntouchedWarmLead(workflow, introContacts)) {
    return `${introPath} No outreach, notes, or scheduled follow-up are recorded yet.`;
  }

  if (introTrackingSummary) {
    return `${introPath} ${introTrackingSummary}`;
  }

  return workflow.lastContactAt
    ? `${introPath} Workflow activity already exists, with last contact logged at ${workflow.lastContactAt}.`
    : `${introPath} Workflow activity already exists on the relationship.`;
}

function deriveWarmLead(
  item: EvWorkstationProject,
  workflow: EvWorkstationProject["workflow"]
): EvWorkstationProject["warmLead"] {
  const hasWarmLead =
    item.warmLead.hasWarmLead ||
    item.warmLead.matches.length > 0 ||
    item.warmLead.matchedFundNames.length > 0;
  const untouched = hasWarmLead && isUntouchedWarmLead(workflow, item.introContacts);

  return {
    ...item.warmLead,
    hasWarmLead,
    contactCount: item.warmLead.matches.length,
    untouched,
    outreachGaps: hasWarmLead ? getWarmLeadOutreachGaps(workflow, item.introContacts) : [],
    summary: hasWarmLead ? buildWarmLeadSummary(item, workflow, item.introContacts) : null,
  };
}

function normalizeWorkflowPatch(patch: EvWorkflowPatch): EvWorkflowPatch {
  const normalized: EvWorkflowPatch = { ...patch };

  if (Object.hasOwn(normalized, "projectFollowUpDate")) {
    normalized.followUpDate = normalized.projectFollowUpDate ?? null;
  } else if (Object.hasOwn(normalized, "followUpDate")) {
    normalized.projectFollowUpDate = normalized.followUpDate ?? null;
  }

  return normalized;
}

function deriveFlags(
  item: EvWorkstationProject,
  warmLead: EvWorkstationProject["warmLead"]
): EvWorkstationProject["flags"] {
  return deriveWorkflowFlags({
    workflow: item.workflow,
    projectIsArchived: item.project.is_archived,
    warmLead,
    latestHandoff: item.latestHandoff,
  });
}

export function applyOptimisticWorkflowPatch(
  item: EvWorkstationProject,
  patch: EvWorkflowPatch
): EvWorkstationProject {
  const normalizedPatch = normalizeWorkflowPatch(patch);
  const workflow = {
    ...item.workflow,
    ...normalizedPatch,
  };

  const nextItem = {
    ...item,
    workflow,
  };
  const warmLead = deriveWarmLead(nextItem, workflow);

  return {
    ...nextItem,
    warmLead,
    flags: deriveFlags(nextItem, warmLead),
  };
}

export function applyOptimisticIntroContactPatch(
  item: EvWorkstationProject,
  update: EvIntroContactUpdateInput
): EvWorkstationProject {
  const timestamp = new Date().toISOString();
  const introContacts = item.introContacts ?? [];
  const existingIndex = introContacts.findIndex(
    (contact) => contact.contactKey === update.contactKey
  );
  const nextIntroContacts = [...introContacts];

  if (existingIndex >= 0) {
    const existing = nextIntroContacts[existingIndex];
    if (existing) {
      const statusChanged = existing.status !== update.status;
      nextIntroContacts[existingIndex] = {
        ...existing,
        fundName: update.fundName,
        normalizedFundName: update.normalizedFundName,
        status: update.status,
        notes:
          update.notes !== undefined
            ? normalizeNullableString(update.notes ?? null)
            : existing.notes,
        isCurrentWarmMatch: true,
        lastStatusChangedAt: statusChanged ? timestamp : existing.lastStatusChangedAt,
        updatedAt: timestamp,
        contact: {
          ...existing.contact,
          ...update.contact,
          role:
            update.contact.role !== undefined
              ? normalizeNullableString(update.contact.role ?? null)
              : existing.contact.role,
          email:
            update.contact.email !== undefined
              ? normalizeNullableString(update.contact.email ?? null)
              : existing.contact.email,
          telegram:
            update.contact.telegram !== undefined
              ? normalizeNullableString(update.contact.telegram ?? null)
              : existing.contact.telegram,
          linkedin:
            update.contact.linkedin !== undefined
              ? normalizeNullableString(update.contact.linkedin ?? null)
              : existing.contact.linkedin,
          twitter:
            update.contact.twitter !== undefined
              ? normalizeNullableString(update.contact.twitter ?? null)
              : existing.contact.twitter,
          directContactOwners: uniqueStrings(
            update.contact.directContactOwners ?? existing.contact.directContactOwners
          ),
          isTelegramGroup:
            update.contact.isTelegramGroup !== undefined
              ? update.contact.isTelegramGroup
              : existing.contact.isTelegramGroup,
        },
      };
    }
  } else {
    nextIntroContacts.push({
      id: null,
      projectId: item.project.id,
      workflowId: item.workflow.id,
      contactKey: update.contactKey,
      fundName: update.fundName,
      normalizedFundName: update.normalizedFundName,
      status: update.status,
      notes: normalizeNullableString(update.notes ?? null),
      isCurrentWarmMatch: true,
      lastStatusChangedAt: timestamp,
      createdAt: timestamp,
      updatedAt: timestamp,
      contact: {
        name: update.contact.name,
        role: normalizeNullableString(update.contact.role ?? null),
        email: normalizeNullableString(update.contact.email ?? null),
        telegram: normalizeNullableString(update.contact.telegram ?? null),
        linkedin: normalizeNullableString(update.contact.linkedin ?? null),
        twitter: normalizeNullableString(update.contact.twitter ?? null),
        directContactOwners: uniqueStrings(update.contact.directContactOwners ?? []),
        isTelegramGroup: update.contact.isTelegramGroup === true,
      },
    });
  }

  const workflow = { ...item.workflow };
  const warmIntroLane = deriveWarmIntroFollowUpLane(nextIntroContacts);
  workflow.warmIntroFollowUpDate = warmIntroLane.warmIntroFollowUpDate;
  workflow.warmIntroFollowUpReason = warmIntroLane.warmIntroFollowUpReason;

  const projectFollowUpDate = deriveIntroMadeProjectFollowUpDate(
    nextIntroContacts,
    workflow.projectFollowUpDate ?? workflow.followUpDate ?? null
  );
  if (projectFollowUpDate !== undefined) {
    workflow.projectFollowUpDate = projectFollowUpDate;
    workflow.followUpDate = projectFollowUpDate;
  }

  const nextItem = {
    ...item,
    workflow,
    introContacts: nextIntroContacts,
  };
  const warmLead = deriveWarmLead(nextItem, workflow);

  return {
    ...nextItem,
    warmLead,
    flags: deriveFlags(nextItem, warmLead),
  };
}
